import React from 'react';

// Sidebar skeleton component
export const SidebarSkeleton: React.FC = () => {
  return (
    <div className="hidden lg:block fixed top-0 left-0 h-full w-64 bg-kind-graphite border-r border-kind-container/50">
      <div className="p-6 h-full flex flex-col">
        {/* Logo skeleton */}
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-8 h-8 bg-kind-container rounded-lg animate-pulse"></div>
          <div className="h-6 bg-kind-container rounded-lg animate-pulse w-16"></div>
        </div>

        {/* Navigation items skeleton */}
        <div className="space-y-1 flex-1">
          {/* Home - highlighted */}
          <div className="h-10 bg-kind-purple/30 rounded-lg animate-pulse"></div>
          {/* myOpenSelf with coming soon */}
          <div className="h-10 bg-kind-container rounded-lg animate-pulse"></div>
          {/* Profile */}
          <div className="h-10 bg-kind-container rounded-lg animate-pulse"></div>
          {/* Subscription */}
          <div className="h-10 bg-kind-container rounded-lg animate-pulse"></div>
          {/* Settings */}
          <div className="h-10 bg-kind-container rounded-lg animate-pulse"></div>
        </div>

        {/* User info skeleton at bottom */}
        <div className="mt-auto">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-kind-container rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 bg-kind-container rounded-lg animate-pulse mb-1"></div>
              <div className="h-3 bg-kind-container rounded-lg animate-pulse w-3/4"></div>
            </div>
          </div>
          <div className="h-8 bg-kind-container rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

// Main content skeleton component (non-sidebar) - matches MainContent.tsx structure
export const MainContentSkeleton: React.FC<{ className?: string }> = (props) => {
  return (
    <div className={`flex-1 overflow-y-auto bg-kind-deep ${props.className}`}>
      {/* Hero Section Skeleton - matches HeroSection component exactly */}
      <div className="relative mx-2 lg:mx-8 mt-2 lg:mt-8 rounded-xl lg:rounded-2xl overflow-hidden h-[60dvh]">
        {/* Background Image placeholder */}
        <div className="absolute inset-0 bg-kind-container/30 animate-pulse"></div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

        {/* Content - matches HeroSection layout */}
        <div className="relative z-10 h-full flex flex-col justify-end p-4 lg:p-16">
          <div className="max-w-2xl">
            {/* Mobile Layout Skeleton */}
            <div className="lg:hidden">
              <div className="h-8 bg-kind-text-primary/20 rounded-lg animate-pulse mb-3 w-48"></div>
              <div className="h-4 bg-kind-text-primary/20 rounded-lg animate-pulse mb-4 w-full"></div>
              <div className="space-y-3 mb-4">
                <div className="h-12 bg-kind-purple/30 rounded-xl animate-pulse w-full"></div>
                <div className="h-12 bg-kind-container/50 rounded-xl animate-pulse w-full"></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="h-6 bg-kind-teal/50 rounded-full animate-pulse w-16"></div>
                <div className="h-4 bg-kind-text-primary/20 rounded-lg animate-pulse w-24"></div>
                <div className="h-4 bg-kind-text-primary/20 rounded-lg animate-pulse w-20"></div>
              </div>
            </div>

            {/* Desktop Layout Skeleton */}
            <div className="hidden lg:block">
              <div className="h-16 bg-kind-text-primary/20 rounded-lg animate-pulse mb-6 w-80"></div>
              <div className="h-6 bg-kind-text-primary/20 rounded-lg animate-pulse mb-8 w-96"></div>
              <div className="flex space-x-6 mb-8">
                <div className="h-14 bg-kind-purple/30 rounded-2xl animate-pulse w-48"></div>
                <div className="h-14 bg-kind-container/50 rounded-2xl animate-pulse w-48"></div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="h-8 bg-kind-teal/50 rounded-full animate-pulse w-20"></div>
                <div className="h-4 bg-kind-text-primary/20 rounded-lg animate-pulse w-32"></div>
                <div className="h-4 bg-kind-text-primary/20 rounded-lg animate-pulse w-24"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Companion Carousel Skeleton - matches CompanionCarousel component exactly */}
      <div className="py-8 lg:py-12 space-y-12 lg:space-y-16">
        <section className="px-4 lg:px-8 CompanionCarousel">
          <div className="flex overflow-x-auto space-x-4 lg:space-x-6 pb-6 scrollbar-hide">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="group relative flex-shrink-0 w-64 lg:w-80">
                <div className="relative overflow-hidden rounded-2xl bg-kind-container border border-kind-container/50 animate-pulse">
                  {/* Image placeholder */}
                  <div className="w-full h-40 lg:h-48 bg-kind-graphite"></div>

                  {/* Overlay with content */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                    {index === 0 && (
                      <div className="absolute top-4 right-4">
                        <div className="bg-kind-purple/50 rounded-full animate-pulse w-16 h-6"></div>
                      </div>
                    )}
                    <div className="absolute bottom-4 lg:bottom-6 left-4 lg:left-6 right-4 lg:right-6">
                      <div className="h-6 lg:h-8 bg-kind-text-primary/20 rounded-lg animate-pulse mb-2 lg:mb-3 w-32"></div>
                      <div className="flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="h-8 lg:h-10 bg-kind-purple/30 rounded-2xl animate-pulse w-20 lg:w-24"></div>
                        <div className="p-2 lg:p-3 bg-kind-container/50 rounded-full animate-pulse w-8 lg:w-10 h-8 lg:h-10"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info section - matches CompanionCard */}
                <div className="p-4 lg:p-6 bg-kind-container">
                  <div className="space-y-3 lg:space-y-4 mb-3 lg:mb-4">
                    <div className="h-4 bg-kind-text-secondary/30 rounded-lg animate-pulse"></div>
                    <div className="h-4 bg-kind-text-secondary/30 rounded-lg animate-pulse w-3/4"></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 lg:space-x-3">
                      <div className="h-6 bg-kind-teal/50 rounded-full animate-pulse w-16"></div>
                      <div className="h-4 bg-kind-text-secondary/30 rounded-lg animate-pulse w-8"></div>
                    </div>
                    <div className="h-4 bg-kind-purple/30 rounded-lg animate-pulse w-16"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// Loading indicator component
export const LoadingIndicator: React.FC = () => {
  return (
    <div className="fixed bottom-6 right-6 bg-kind-graphite/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-kind-container/50">
      <div className="flex items-center space-x-2">
        <div className="animate-pulse flex space-x-1">
          <div className="w-2 h-2 bg-kind-purple rounded-full"></div>
          <div className="w-2 h-2 bg-kind-purple rounded-full"></div>
          <div className="w-2 h-2 bg-kind-purple rounded-full"></div>
        </div>
        <span className="text-sm text-kind-text-secondary">Loading...</span>
      </div>
    </div>
  );
};

// Full app skeleton component (with sidebar)
export const AppSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-kind-deep">
      <SidebarSkeleton />
      <MainContentSkeleton className="lg:ml-64" />
      <LoadingIndicator />
    </div>
  );
};

export default AppSkeleton;
